import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import { Upload } from '@progress/kendo-react-upload';
import { UserAuth } from '../../auth/Auth';
import { PrivateAxiosFile } from '../../../environment/AxiosInstance';
import { ErrorMessage, SuccessMessage } from '../../../environment/ToastMessage';
import { useParams } from 'react-router-dom';

const priorityOptions = [
  { id: "high", name: "High" },
  { id: "medium", name: "Medium" },
  { id: "low", name: "Low" },
];

const AddTaskModal = ({ show, handleClose, projectId, onSuccess, onUpdated }) => {
  const { User, MatchPermission } = UserAuth();
  const { id } = useParams();
  if (projectId === undefined) {
    projectId = id;
  }
  // ================== Form States ==================
  const [taskName, setTaskName] = useState("");
  const [doer, setDoer] = useState([]); 
  const [priority, setPriority] = useState(null);
  const [description, setDescription] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [loading, setLoading] = useState(false);

  // NEW: Manage upload files state
  const [uploadFiles, setUploadFiles] = useState([]);

  // ================== Detect Theme ==================
  useEffect(() => {
    const checkTheme = () => setIsDarkTheme(document.body.classList.contains("dark-theme"));
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // ================== PERMISSION CHECK ==================
  if (!MatchPermission(['Add Project', 'Project All'])) {
    return null;
  }

  // ================== Handle Submit ==================
  const handleCreateTask = async () => {
    if (!MatchPermission(['Add Project', 'Project All', 'Doer Edit Project'])) {
      ErrorMessage("You don't have permission to create tasks");
      return;
    }

    if (!taskName || !doer || !priority || !dateRange[0] || !dateRange[1]) {
      ErrorMessage("Please fill all required fields");
      return;
    }

    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("task_name", taskName);
    doer.forEach((user) => {
    formData.append("assigned_to[]", user.id);
  });
    formData.append("priority", priority.id);
    formData.append("description", description);
    formData.append("start_date", dateRange[0].toISOString());
    formData.append("end_date", dateRange[1].toISOString());


    formData.append("file", uploadFiles[0]);




    try {
      setLoading(true);
      const res = await PrivateAxiosFile.post("projecttasks/create", formData);

      if (res.data?.success) {
        SuccessMessage("Task created successfully");
        if (onUpdated) onUpdated(); 
        handleClose();
      } else {
        ErrorMessage(res.data?.message || "Failed to create task");
      }
    } catch (err) {
      console.error("Error creating task:", err);
      ErrorMessage("Error creating task");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal id="AddPTaskModal" show={show} onHide={handleClose} backdrop="static" centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="gth-modal-title">Add Task</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-1">
        <div className="row">
          {/* Task Name */}
          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Task Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Task Name"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
              />
            </div>
          </div>

          {/* Doer */}
          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Doer</label>
              <div className="custom-select-wrap">
                <Select
                  options={User}
                  isMulti
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={doer}
                  onChange={setDoer}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary25: "#ddddff",
                      primary: "#6161ff",
                    },
                  })}
                  styles={{
                    menu: (base) => ({
                      ...base,
                      backgroundColor: isDarkTheme ? "#06162f" : "#ffffff",
                      color: isDarkTheme ? "#e0e0e0" : "#000000",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: isDarkTheme ? "#e0e0e0" : "#000000",
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Start Date */}
          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Task Start Date</label>
              <div className="exp-datepicker-cont">
                <span className="cal-icon"><i className="fas fa-calendar-alt"></i></span>
                <DatePicker
                  selected={dateRange[0]}
                  onChange={(date) => setDateRange([date, dateRange[1]])}
                  placeholderText="Select Start Date"
                />
              </div>
            </div>
          </div>

          {/* End Date */}
          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Task End Date</label>
              <div className="exp-datepicker-cont">
                <span className="cal-icon"><i className="fas fa-calendar-alt"></i></span>
                <DatePicker
                  selected={dateRange[1]}
                  onChange={(date) => setDateRange([dateRange[0], date])}
                  placeholderText="Select End Date"
                />
              </div>
            </div>
          </div>

          {/* Priority */}
          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <div className="custom-select-wrap">
                <Select
                  options={priorityOptions}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={priority}
                  onChange={setPriority}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
            </div>
          </div>

          {/* Upload */}
          <div className="col-12">
            <div className="form-group">
              {/* <Upload
                batch={false} // single file mode
                multiple={false} // ensure multiple files cannot be selected
                autoUpload={false} // manual upload
                defaultFiles={[]}
                withCredentials={false}
                onAdd={(e) => {
                  console.log(e.newState, 'add event');
                  // Use the latest file added (newState always contains current file)
                  setUploadFiles(e.newState);
                }}
                onRemove={(e) => {
                  const removedUids = new Set(e.files.map(f => f.uid));
                  setUploadFiles(prev => prev.filter(f => !removedUids.has(f.uid)));
                }}
              /> */}
              <input type="file" onChange={(e) => setUploadFiles(e.target.files)} className='form-control' />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="gth-blue-light-bg">
        <button type="button" className="btn" onClick={handleClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-exp-green"
          onClick={handleCreateTask}
          disabled={loading}
        >
          {loading ? "Creating..." : "Create"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTaskModal;
